import NoData from '@/components/common/NoData'
import useSearchInterfaceId from '@/features/admin/hooks/interface/useSearchInterfaceId'
import { useAuthContext } from '@/features/auth/context'
import { AlertResponse, MetadataFieldDTO } from '@/types/notification'
import { getTimeFromGMTandTimestamp } from '@/ultils'
import { Stack, Typography } from '@mui/material'
import ErrorNoti from 'components/images/Attention.png'
import HardwareNoti from 'components/images/HardwareNoti.png'
import NotifImage from 'components/images/NotifImg.png'
import ProgramNoti from 'components/images/ProgramNoti.png'
import StationNoti from 'components/images/StationNoti.png'
import classes from './styles.module.scss'
import useTranslation from '@/hooks/useTranslation'
import { useUnits } from '@/hooks/useUnits'

interface ListAlertsProps {
  rows: AlertResponse[]
}

const ListAlerts = ({ rows }: ListAlertsProps) => {
  const { t } = useTranslation()
  const { convertFromMetricV2, getImperialUnitLabel } = useUnits()

  const { interfaceId } = useAuthContext()
  const { data: dataInterface } = useSearchInterfaceId(interfaceId+"")

  const convertMetadataArrayToObject = (metadata?: MetadataFieldDTO[] | null): Record<string, any> => {
    try {
      if (!metadata || !Array.isArray(metadata)) return {}

      const result: Record<string, any> = {}
      metadata.forEach((item) => {
        if (item.field) {
          const hasUnit = item.unit && item.unit.trim() !== ''
          result[item.field] = hasUnit ? `${item.value} ${item.unit}` : item.value
        }
      })
      return result
    } catch (error) {
      console.error('Error converting metadata array to object:', error)
      return {}
    }
  }

  const convertMetadataToImperial = (metadata?: Record<string, any>) => {
    try {
      if (!metadata) return {}

      const converted: Record<string, any> = {}

      Object.entries(metadata).forEach(([key, value]) => {
        const stringValue = String(value)
        const parts = stringValue.split(' ')

        if (parts.length === 2) {
          const numericValue = parseFloat(parts[0])
          const unit = parts[1]

          if (!isNaN(numericValue) && unit) {
            const convertedValue = convertFromMetricV2(numericValue, unit)
            const convertedUnit = getImperialUnitLabel(unit)
            converted[key] = `${convertedValue} ${convertedUnit}`
          } else {
            converted[key] = value
          }
        } else {
          converted[key] = value
        }
      })

      return converted
    } catch (error) {
      console.error('Error converting metadata to imperial:', error)
      return metadata || {}
    }
  }

  const formatTimestamp = (dateString: string): string => {
    const timestamp = new Date(dateString).getTime()
    return getTimeFromGMTandTimestamp(dataInterface?.timeZone ?? '', timestamp)
  }

  const getNotificationGroupIcon = (tags?: string[] | null) => {
    if (!tags || tags.length === 0) return <img src={NotifImage} alt="No img" style={{ width: '100%', height: 'auto' }} />

    const tag = tags[0]
    switch (tag) {
      case 'Water':
      case 'WaterTankLevel':
        return <img src={NotifImage} alt="No img" style={{ width: '100%', height: 'auto' }} />
      case 'ProgramStateChange':
      case 'ProgramIssues':
        return <img src={ProgramNoti} alt="No img" style={{ width: '100%', height: 'auto' }} />
      case 'StationIssues':
        return <img src={StationNoti} alt="No img" style={{ width: '100%', height: 'auto' }} />
      case 'Hardware':
      case 'HardwareIssues':
      case 'WaterEngineIssues':
        return <img src={HardwareNoti} alt="No img" style={{ width: '100%', height: 'auto' }} />
      default:
        return <img src={NotifImage} alt="No img" style={{ width: '100%', height: 'auto' }} />
    }
  }

  return (
    <Stack className={classes.root}>
      {rows.map((item, index) => (
        <Stack
          key={index}
          className={classes.boxAlerts}
          sx={{
            borderBottom: index !== rows.length - 1 ? '1px solid #555' : 'none',
          }}
        >
          <Stack direction="row" spacing={2}>
            <Stack className={classes.groupIcon}>
              {item.type === 'Error' ? (
                <img src={ErrorNoti} alt="No img" style={{ width: '100%', height: 'auto' }} />
              ) : (
                getNotificationGroupIcon(item.tags)
              )}
            </Stack>
            <Stack>
              <Typography className={classes.txtNotification}>
                {item.messageCode ? t(`${item.messageCode}.content`, convertMetadataToImperial(convertMetadataArrayToObject(item.metadata))) : '-'}
              </Typography>
              <Typography className={classes.txtDate}>
                {formatTimestamp(item.createdAt)}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      ))}
      {rows && rows?.length === 0 && (
        <NoData text={ t('noData') } colorText="white" />
      )}
    </Stack>
  )
}

export default ListAlerts
